import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Nav from "../components/Nav";
import { api, formatApiError } from "../lib/api";
import { Phone, ArrowLeft, CheckCircle, WarningCircle, Flag, FacebookLogo, InstagramLogo, TiktokLogo, YoutubeLogo, LinkedinLogo, XLogo, WhatsappLogo, Globe, DownloadSimple, FilePdf, Eye, Bank, X as XIcon } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LINK_ICONS = {
  website: Globe,
  facebook: FacebookLogo,
  instagram: InstagramLogo,
  tiktok: TiktokLogo,
  youtube: YoutubeLogo,
  linkedin: LinkedinLogo,
  x: XLogo,
  whatsapp: WhatsappLogo,
  documents: FilePdf,
};
const iconFor = (label) => LINK_ICONS[(label || "").toLowerCase()] || Globe;

// Card-type accent map — mirrors /cards comparison page so the public
// scan tile, the dashboard tile, and the marketing page all use the same
// colour family per type.
const CARD_TYPE_VISUALS = {
  curb_crew:      { label: "Curb Crew · Tip-friendly", stripBg: "bg-[color:var(--cc-yellow)]", stripText: "text-black", codeBg: "bg-[color:var(--cc-yellow)]" },
  curb_pro:       { label: "Curb Pro · Service Provider", stripBg: "bg-black",                  stripText: "text-white", codeBg: "bg-[color:var(--cc-yellow)]" },
  curb_assets:    { label: "Curb Retailer · Shopfront + Stock", stripBg: "bg-[#C2410C]",       stripText: "text-white", codeBg: "bg-white border border-black" },
  curb_medic:     { label: "Curb Medic · MEDICAL ALERT", stripBg: "bg-[#DC2626]",                stripText: "text-white", codeBg: "bg-white border border-black" },
  curb_traveller: { label: "Curb Traveller", stripBg: "bg-[#0EA5E9]", stripText: "text-white", codeBg: "bg-white border border-black" },
  curb_auto:      { label: "Curb Auto · Vehicle", stripBg: "bg-[#16A34A]", stripText: "text-white", codeBg: "bg-white border border-black" },
};
const visualFor = (type) => CARD_TYPE_VISUALS[type] || CARD_TYPE_VISUALS.curb_pro;

const MEDICAL_AID_LABELS = {
  no_medical_aid: "No medical aid",
  hospital_plan:  "Hospital plan",
  medical_plan:   "Medical plan",
};


// Make sure every saved link has a usable scheme. Defensive: covers any rows
// that were written before the backend started auto-prefixing https://.
const normaliseLinkUrl = (raw) => {
  const u = (raw || "").trim();
  if (!u) return "";
  if (/^(https?:|mailto:|tel:)/i.test(u)) return u;
  return `https://${u.replace(/^\/+/, "")}`;
};

export default function CardProfile() {
  const { code } = useParams();
  const [data, setData] = useState(null); // {cardholder, status?}
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [formError, setFormError] = useState("");
  const [showLost, setShowLost] = useState(false);
  const [lostForm, setLostForm] = useState({ reporter_name: "", reporter_phone: "", note: "" });
  const [lostSubmitted, setLostSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/cardholder/${code}`);
        setData(data);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!/^0\d{9}$/.test(form.phone)) return setFormError("Enter a valid 10-digit SA number starting with 0");
    if (!form.name.trim()) return setFormError("Please enter your name");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/cardholder/${code}/interest`, form);
      setRevealed(data);
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitLost = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/public/cardholder/${code}/report-lost`, lostForm);
      setLostSubmitted(true);
    } catch (err) {
      setFormError(formatApiError(err));
    }
  };

  if (error)
    return (
      <div>
        <Nav />
        <div className="max-w-xl mx-auto px-4 py-20 text-center">
          <div className="font-display font-black text-3xl">Card not found</div>
          <p className="mt-2 text-neutral-600">{error}</p>
          <Link to="/lookup" className="inline-flex mt-6 label-tag px-4 py-3 border border-black brutal-btn">
            <ArrowLeft size={14} weight="bold" className="mr-2" /> Back to lookup
          </Link>
        </div>
      </div>
    );

  if (!data) return <div><Nav /><div className="px-4 py-20 text-center label-tag">Loading…</div></div>;

  // Code exists but card not yet activated → jump straight into the
  // activation form. We render a thin "redirecting…" frame for the
  // single render before the navigate fires (using replace so the back
  // button doesn't bounce them between profile and activator).
  if (data.status === "not_activated" || !data.cardholder) {
    return <NotActivatedRedirect code={code.toUpperCase()} />;
  }

  const ch = data.cardholder;

  // Card has been reported lost
  if (ch.lost_reported || ch.is_active === false) {
    return (
      <div>
        <Nav />
        <section className="border-b border-black bg-[color:var(--cc-yellow)]" data-testid="lost-card-page">
          <div className="max-w-xl mx-auto px-4 py-16 text-center">
            <WarningCircle size={48} weight="fill" className="mx-auto" />
            <div className="label-tag mt-4">Card #{ch.code}</div>
            <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tighter mt-3">
              This card has been reported lost.
            </h1>
            <p className="mt-4 text-neutral-800">
              Please return it to the nearest Curb Crew agent or hardware store. The cardholder's details have been protected and are no longer publicly visible.
            </p>
            <Link to="/" className="inline-flex mt-6 label-tag px-4 py-3 bg-black text-white brutal-btn">
              Back to home
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <Nav />
      <section className="border-b border-black bg-[color:var(--cc-bg-2)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <Link to="/lookup" className="label-tag inline-flex items-center gap-2" data-testid="back-to-lookup">
            <ArrowLeft size={14} weight="bold" /> Back
          </Link>

          <div className="mt-6 bg-white brutal-border brutal-shadow overflow-hidden lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]" data-testid="cardholder-card">
            {ch.picture_url && (
              <div className="aspect-square sm:aspect-[4/3] lg:aspect-auto lg:h-full bg-black overflow-hidden border-b lg:border-b-0 lg:border-r border-black">
                <img
                  src={`${BACKEND}${ch.picture_url}`}
                  alt={ch.full_name}
                  className="w-full h-full object-cover"
                  data-testid="cardholder-photo"
                />
              </div>
            )}
            <div className="p-6 sm:p-10">
              {/* Card-type accent strip — pulls the same colour as /cards
                  and the dashboard tile so visitors instantly clock which
                  flavour of Curb Crew card they're looking at. */}
              {(() => { const vis = visualFor(ch.card_type); return (
                <div className={`${vis.stripBg} ${vis.stripText} border border-black px-3 py-1.5 label-tag inline-flex items-center gap-2 mb-4`} data-testid="public-card-type-strip">
                  {vis.label}
                </div>
              ); })()}
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tighter" data-testid="cardholder-name">
                    {ch.full_name}
                  </h1>
                  {ch.location && <div className="label-tag mt-2 text-neutral-600">{ch.location}</div>}
                </div>
                <div className={`${visualFor(ch.card_type).codeBg} px-3 py-2 font-mono-cc text-sm`} data-testid="cardholder-code">
                  #{ch.code}
                </div>
              </div>

              {typeof ch.scans_this_week === "number" && ch.scans_this_week > 0 && (
                <div
                  className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-black text-white border border-black label-tag"
                  data-testid="scan-counter"
                >
                  <Eye size={14} weight="bold" />
                  {ch.scans_this_week} {ch.scans_this_week === 1 ? "scan" : "scans"} this week
                </div>
              )}

              <div className="mt-6 border-t border-black pt-6">
                <div className="label-tag mb-3">Skills</div>
                <div className="flex flex-wrap gap-2" data-testid="cardholder-skills">
                  {(ch.skills || []).map((s, i) => (
                    <span key={i} className="label-tag bg-black text-white px-3 py-2 border border-black">
                      {s}
                    </span>
                  ))}
                </div>
                <div className="label-tag text-neutral-500 mt-4" data-testid="popia-notice">
                  Cardholder consented under POPIA · Phone stays private until requested
                </div>
              </div>

              {/* Curb Medic public block — backend already strips these
                  fields for non-medic cards, so the conditional below is
                  defensive belt-and-braces. Renders ONLY rows that have
                  actual values so an empty medic profile doesn't show a
                  wall of dashes. */}
              {ch.card_type === "curb_medic" && (ch.medical_allergies || ch.medical_chronic || ch.medical_blood_group || ch.medical_aid_plan || ch.medical_emergency_contact) && (
                <div className="mt-6 border-2 border-[#DC2626] bg-[#FEF2F2] p-4" data-testid="public-medic-block">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="label-tag bg-[#DC2626] text-white px-2 py-1 inline-block">MEDICAL ALERT · READ IN A CRISIS</span>
                  </div>
                  <dl className="space-y-2 text-sm">
                    {ch.medical_allergies && (
                      <div className="flex flex-col sm:flex-row sm:gap-3 border-t border-[#DC2626]/30 pt-2" data-testid="medic-pub-allergies">
                        <dt className="label-tag sm:w-44 text-[#DC2626] shrink-0">Allergies</dt>
                        <dd className="font-bold text-neutral-900">{ch.medical_allergies}</dd>
                      </div>
                    )}
                    {ch.medical_chronic && (
                      <div className="flex flex-col sm:flex-row sm:gap-3 border-t border-[#DC2626]/30 pt-2" data-testid="medic-pub-chronic">
                        <dt className="label-tag sm:w-44 text-[#DC2626] shrink-0">Chronic condition</dt>
                        <dd className="font-bold text-neutral-900">{ch.medical_chronic}</dd>
                      </div>
                    )}
                    {ch.medical_blood_group && (
                      <div className="flex flex-col sm:flex-row sm:gap-3 border-t border-[#DC2626]/30 pt-2" data-testid="medic-pub-blood">
                        <dt className="label-tag sm:w-44 text-[#DC2626] shrink-0">Blood group</dt>
                        <dd className="font-bold font-mono-cc text-neutral-900">{ch.medical_blood_group}</dd>
                      </div>
                    )}
                    {ch.medical_aid_plan && (
                      <div className="flex flex-col sm:flex-row sm:gap-3 border-t border-[#DC2626]/30 pt-2" data-testid="medic-pub-aid">
                        <dt className="label-tag sm:w-44 text-[#DC2626] shrink-0">Medical aid</dt>
                        <dd className="font-bold text-neutral-900">{MEDICAL_AID_LABELS[ch.medical_aid_plan] || ch.medical_aid_plan}</dd>
                      </div>
                    )}
                    {ch.medical_emergency_contact && (
                      <div className="flex flex-col sm:flex-row sm:gap-3 border-t border-[#DC2626]/30 pt-2" data-testid="medic-pub-emergency">
                        <dt className="label-tag sm:w-44 text-[#DC2626] shrink-0">Emergency contact</dt>
                        <dd>
                          <a href={`tel:${ch.medical_emergency_contact}`} className="font-bold font-mono-cc text-[#DC2626] underline text-lg">
                            {ch.medical_emergency_contact}
                          </a>
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              {ch.bio && (
                <div className="mt-6 border-t border-black pt-6">
                  <div className="label-tag mb-2">About</div>
                  <p className="text-neutral-800 leading-relaxed whitespace-pre-wrap">{ch.bio}</p>
                </div>
              )}

              {/* Notice Board — short, time-bound updates the cardholder
                  publishes on their own card. Auto-expires server-side. */}
              {Array.isArray(ch.notice_posts) && ch.notice_posts.length > 0 && (
                <div className="mt-6 border-t border-black pt-6" data-testid="public-notice-board">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-black text-[color:var(--cc-yellow)] px-2 py-1 label-tag">LATEST</span>
                    <div className="label-tag">From {(ch.full_name || "this card").split(" ")[0]}</div>
                  </div>
                  <ul className="space-y-2">
                    {ch.notice_posts.map((p) => (
                      <li
                        key={p.id}
                        className="border-2 border-black bg-[color:var(--cc-bg-2)] p-3"
                        data-testid={`public-notice-${p.id}`}
                      >
                        <div className="font-display font-black text-base tracking-tight leading-tight">{p.title}</div>
                        <div className="text-sm text-neutral-800 mt-1 whitespace-pre-wrap leading-snug">{p.body}</div>
                        <div className="label-tag text-neutral-500 mt-2 text-[10px]">
                          {new Date(p.created_at).toLocaleDateString("en-ZA", { day: "numeric", month: "short" })}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Curb Traveller doc-vault unlock — only renders when the
                  backend says vault_unlock=true (i.e. card_type=curb_traveller
                  AND vault_active=true). The link itself never reaches this
                  component — we only show it after the visitor enters a PIN
                  that we mail to the cardholder. See VaultUnlockCard. */}
              {ch.vault_unlock && (
                <VaultUnlockCard code={ch.code} cardholderName={ch.full_name} />
              )}

              {Array.isArray(ch.links) && ch.links.length > 0 && (
                <div className="mt-6 border-t border-black pt-6" data-testid="cardholder-links">
                  <div className="label-tag mb-3">Find me online · tap to open</div>
                  <div className="flex flex-col gap-2">
                    {ch.links.map((l) => {
                      const Icon = iconFor(l.label);
                      const safeUrl = normaliseLinkUrl(l.url);
                      let pretty = safeUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");
                      if (pretty.length > 38) pretty = pretty.slice(0, 36) + "…";
                      return (
                        <a
                          key={l.label + l.url}
                          href={safeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const win = window.open(safeUrl, "_blank", "noopener,noreferrer");
                            if (!win) window.location.href = safeUrl;
                          }}
                          className="group relative flex items-center gap-3 px-4 py-3 bg-[color:var(--cc-yellow)] border border-black brutal-btn cursor-pointer transition active:translate-y-0.5"
                          data-testid={`link-${(l.label || "").toLowerCase()}`}
                        >
                          <Icon size={22} weight="bold" className="flex-shrink-0" style={{ pointerEvents: "none" }} />
                          <div className="flex-1 min-w-0" style={{ pointerEvents: "none" }}>
                            <div className="font-display font-black text-base leading-none">{l.label}</div>
                            <div className="font-mono-cc text-[11px] text-neutral-700 mt-1 truncate">{pretty}</div>
                          </div>
                          <span className="font-display font-black text-xl leading-none" style={{ pointerEvents: "none" }} aria-hidden>↗</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-8 border-t border-black pt-6">
                {/* Save to phone contacts — always available, phone only included if cardholder opted in */}
                <a
                  href={`${BACKEND}/api/public/cardholder/${ch.code}/vcard`}
                  download={`${(ch.full_name || "curbcrew").replace(/[^A-Za-z0-9_-]/g, "_")}.vcf`}
                  className="w-full inline-flex items-center justify-center gap-2 bg-white label-tag px-6 py-4 border-2 border-black brutal-btn mb-3"
                  data-testid="save-vcard-btn"
                >
                  <DownloadSimple size={18} weight="bold" /> Save to my phone contacts
                </a>
                <div className="label-tag text-neutral-500 mb-4 -mt-1">
                  Stores {ch.full_name?.split(" ")[0] || "this card"}'s details in your phone book — no data needed to find them again.
                </div>

                {/* PayShap "Send me money" — shows the cardholder's PayShap
                    cell number with 3 dead-simple steps. We don't open the
                    visitor's banking app: PayShap routes by cell number, so
                    the visitor just needs to copy the number and paste it
                    into THEIR own bank's PayShap / "Pay by cell" flow. The
                    optional bank badge is informational only — it tells the
                    visitor which bank the money lands in, not which app to
                    open. */}
                {/* PayShap "Send me money" — shows the cardholder's PayShap
                    cell number with 3 dead-simple steps. We don't open the
                    visitor's banking app: PayShap routes by cell number, so
                    the visitor just needs to copy the number and paste it
                    into THEIR own bank's PayShap / "Pay by cell" flow. The
                    optional bank badge is informational only — it tells the
                    visitor which bank the money lands in, not which app to
                    open. Hidden entirely for Specialty cards (medical,
                    vehicle disc, pet ID) — those should be single-purpose.
                    Quick-Pay tip chips also hidden on Business cards even
                    if PayShap is on — tip jars are brand-damaging for an
                    architect or estate agent. */}
                {ch.pay_enabled && ch.pay_phone && ch.card_type !== "curb_medic" && (
                  <PayShapButton
                    name={ch.full_name}
                    payPhone={ch.pay_phone}
                    payBank={ch.pay_bank}
                    tipEnabled={ch.pay_tip_enabled !== false && ch.card_type === "curb_crew"}
                  />
                )}

                {/* Share on WhatsApp — anyone can forward the card */}
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Check out ${ch.full_name || "this Curb Crew card"} 👉 ${typeof window !== "undefined" ? window.location.href : `https://curbcrew.co.za/c/${ch.code}`}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 bg-green-500 label-tag px-6 py-4 border-2 border-black brutal-btn mb-4"
                  data-testid="share-whatsapp-btn"
                >
                  <WhatsappLogo size={18} weight="bold" /> Share on WhatsApp
                </a>

                {!revealed ? (
                  <>
                    {!showForm ? (
                      <button
                        onClick={() => setShowForm(true)}
                        className="w-full sm:w-auto bg-[color:var(--cc-yellow)] label-tag px-6 py-4 brutal-btn flex items-center justify-center gap-2"
                        data-testid="request-contact-btn"
                      >
                        <Phone size={16} weight="bold" /> Request contact number
                      </button>
                    ) : (
                      <form onSubmit={submit} className="space-y-3" data-testid="interest-form">
                        <div className="label-tag">Your details · will be shared with the cardholder</div>
                        <input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 border border-black outline-none focus:ring-2 focus:ring-black" data-testid="interest-name" />
                        <input placeholder="Your SA number (e.g. 0821234567)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9]/g, "") })} maxLength={10} inputMode="numeric" className="w-full px-4 py-3 border border-black outline-none focus:ring-2 focus:ring-black font-mono-cc" data-testid="interest-phone" />
                        <textarea placeholder="Message (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="w-full px-4 py-3 border border-black outline-none focus:ring-2 focus:ring-black" data-testid="interest-message" />
                        {formError && <div className="label-tag text-red-600" data-testid="interest-error">{formError}</div>}
                        <div className="flex gap-3">
                          <button type="submit" disabled={submitting} className="bg-black text-white label-tag px-6 py-4 brutal-btn disabled:opacity-60" data-testid="interest-submit">
                            {submitting ? "Sending…" : "Send & reveal number"}
                          </button>
                          <button type="button" onClick={() => setShowForm(false)} className="label-tag px-6 py-4 border border-black brutal-btn" data-testid="interest-cancel">
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </>
                ) : (
                  <div className="bg-[color:var(--cc-yellow)] border border-black p-6" data-testid="contact-revealed">
                    <div className="flex items-center gap-2 label-tag"><CheckCircle size={14} weight="bold" /> Details sent</div>
                    <div className="font-display font-black text-2xl mt-3 leading-tight">
                      Thanks {form.name.split(" ")[0] || "there"} —<br />
                      we'll pass your message to {revealed?.cardholder_first_name || ch.full_name.split(" ")[0]}.
                    </div>
                    <p className="mt-4 text-sm text-neutral-800 leading-relaxed">
                      Curb Crew has notified the cardholder. They will reach out to you directly on
                      {" "}<span className="font-mono-cc font-semibold">{form.phone}</span> if they're available.
                    </p>
                    <p className="mt-3 text-xs text-neutral-700 leading-relaxed">
                      We protect cardholders' phone numbers, so you won't see them here. Please don't submit again — your details are already on the way.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 text-xs text-neutral-500 leading-relaxed" data-testid="profile-disclaimer">
                Curb Crew is a digital business card. The cardholder is an independent service provider, not employed by Curb Crew. We do not match, place or arrange work and do not take commission on any services that may follow.
              </div>

              <div className="mt-4 border-t border-black pt-4">
                {!showLost && !lostSubmitted && (
                  <button onClick={() => setShowLost(true)} className="label-tag text-neutral-600 hover:text-black flex items-center gap-1" data-testid="report-lost-btn">
                    <Flag size={12} weight="bold" /> Found this card / report as lost
                  </button>
                )}
                {showLost && !lostSubmitted && (
                  <form onSubmit={submitLost} className="space-y-3 mt-2" data-testid="lost-form">
                    <div className="label-tag">Report this card as lost</div>
                    <input placeholder="Your name (optional)" value={lostForm.reporter_name} onChange={(e) => setLostForm({ ...lostForm, reporter_name: e.target.value })} className="w-full px-3 py-2 border border-black text-sm" data-testid="lost-name" />
                    <input placeholder="Your phone (optional)" value={lostForm.reporter_phone} onChange={(e) => setLostForm({ ...lostForm, reporter_phone: e.target.value.replace(/[^0-9]/g, "") })} maxLength={10} className="w-full px-3 py-2 border border-black text-sm font-mono-cc" data-testid="lost-phone" />
                    <textarea placeholder="Where did you find it?" rows={2} value={lostForm.note} onChange={(e) => setLostForm({ ...lostForm, note: e.target.value })} className="w-full px-3 py-2 border border-black text-sm" data-testid="lost-note" />
                    <div className="flex gap-2">
                      <button type="submit" className="label-tag px-4 py-2 bg-black text-white brutal-btn" data-testid="lost-submit">Submit report</button>
                      <button type="button" onClick={() => setShowLost(false)} className="label-tag px-4 py-2 border border-black brutal-btn" data-testid="lost-cancel">Cancel</button>
                    </div>
                  </form>
                )}
                {lostSubmitted && (
                  <div className="bg-[color:var(--cc-yellow)] border border-black p-3 text-sm" data-testid="lost-success">
                    Thank you. The card has been deactivated and Curb Crew admin has been notified.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// PayShap "Send me money" dialog.
//
// IMPORTANT UX NOTE: PayShap routes by cell number, NOT by bank. The visitor
// does NOT need to pick a bank. They just need to:
//   1. Open their OWN bank's app
//   2. Find PayShap / "Pay by cell"
//   3. Paste the cardholder's cell number and pay
//
// An earlier version of this dialog showed a grid of bank tiles, which
// implied the visitor had to tap their own bank — when they picked the
// wrong one, payments failed. We now show the number + clear steps, and
// optionally a trust badge ("Sipho's PayShap bank: Capitec") if the
// cardholder declared it on their dashboard.
function PayShapButton({ name, payPhone, payBank, tipEnabled = true }) {
  const [open, setOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  // null = nothing flashed; number = R{n} chip flashed; "any" = "Can't make up
  // my mind" flashed. Drives the green-pulse confirmation on each chip.
  const [tipFlash, setTipFlash] = React.useState(null);
  const firstName = (name || "this cardholder").split(" ")[0];
  const digits = String(payPhone || "").replace(/\D/g, "");
  // Pretty SA-style "082 123 4567" for the dialog header.
  const pretty = digits.startsWith("0") && digits.length === 10
    ? `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`
    : digits;

  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(digits);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* no clipboard */ }
  };

  // Fixed-amount tip chips. Intentionally low/medium R values to match the
  // car-guard / petrol-attendant / waiter use case. The bank app still requires
  // the visitor to type the amount — but the chip puts the amount on the
  // clipboard (alongside the number, separated by a newline) so they can
  // paste it without thinking.
  const tipAmounts = [5, 10, 20, 30, 40];

  const copyTip = async (amount) => {
    // Combined payload: "082 123 4567\nR20". The number is on line 1 (so most
    // bank apps grab it cleanly on paste), and the amount is right there if
    // the visitor needs it.
    const payload = amount ? `${digits}\nR${amount}` : digits;
    try {
      await navigator.clipboard.writeText(payload);
      setTipFlash(amount ?? "any");
      setTimeout(() => setTipFlash(null), 1400);
    } catch { /* no clipboard */ }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--cc-yellow)] label-tag px-6 py-4 border-2 border-black brutal-btn mb-3"
        data-testid="payshap-btn"
      >
        <Bank size={18} weight="bold" /> Send {firstName} money (PayShap)
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={() => setOpen(false)} data-testid="payshap-dialog">
          <div className="bg-white border-2 border-black w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between bg-black text-white px-4 py-3 sticky top-0">
              <div className="font-display font-black tracking-tight">Send {firstName} money</div>
              <button onClick={() => setOpen(false)} aria-label="Close" className="p-1" data-testid="payshap-close">
                <XIcon size={18} weight="bold" />
              </button>
            </div>
            <div className="p-4">
              {/* The number, big and copy-able. */}
              <div className="label-tag text-neutral-500 mb-2">{firstName}'s PayShap cell number:</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-3 bg-[color:var(--cc-bg-2)] border-2 border-black font-mono-cc text-lg tracking-wide" data-testid="payshap-number">{pretty}</code>
                <button
                  onClick={copyNumber}
                  className={`label-tag px-3 py-3 border-2 border-black brutal-btn ${copied ? "bg-green-500" : "bg-white"}`}
                  data-testid="payshap-copy"
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>

              {/* Optional trust badge — informational only. */}
              {payBank && (
                <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-[color:var(--cc-bg-2)] border border-black text-xs" data-testid="payshap-bank-badge">
                  <Bank size={14} weight="bold" />
                  <span><strong>{firstName}'s PayShap bank:</strong> {payBank} <span className="text-neutral-500">(money lands here)</span></span>
                </div>
              )}

              {/* Tip Jar — R5/R10/R20/R30/R40 quick-tap chips. Tap a chip,
                  the amount + number both land on the clipboard so the
                  visitor can paste straight into their bank app. */}
              {tipEnabled && (
                <div className="mt-4 border-2 border-black p-3 bg-[color:var(--cc-yellow)]" data-testid="payshap-tip-jar">
                  <div className="label-tag mb-1">Pay {firstName}</div>
                  <div className="text-[11px] text-neutral-800 mb-2">Tap a quick amount, or type your own in your bank app.</div>
                  <div className="grid grid-cols-5 gap-2">
                    {tipAmounts.map((amt) => (
                      <button
                        key={amt}
                        type="button"
                        onClick={() => copyTip(amt)}
                        className={`label-tag border-2 border-black brutal-btn px-1 py-3 text-sm font-display font-black tracking-tight transition-colors ${tipFlash === amt ? "bg-green-500 text-black" : "bg-white"}`}
                        data-testid={`payshap-tip-${amt}`}
                      >
                        {tipFlash === amt ? "✓" : `R${amt}`}
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 text-[11px] text-neutral-800 leading-snug">
                    Tapping an amount copies <strong>both</strong> the number and the R-value to your clipboard so you can paste them into your bank app's PayShap screen.
                  </div>
                </div>
              )}

              {/* 3 dead-simple steps. */}
              <ol className="mt-4 space-y-2.5 text-sm" data-testid="payshap-steps">
                <li className="flex gap-2.5">
                  <span className="flex-shrink-0 w-6 h-6 grid place-items-center bg-black text-white font-display font-black text-xs">1</span>
                  <span>Open <strong>your own</strong> banking app (Capitec, FNB, Standard, Absa, Nedbank, Discovery, TymeBank — any SA bank).</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="flex-shrink-0 w-6 h-6 grid place-items-center bg-black text-white font-display font-black text-xs">2</span>
                  <span>Find <strong>"PayShap"</strong> or <strong>"Pay by cell number"</strong>.</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="flex-shrink-0 w-6 h-6 grid place-items-center bg-black text-white font-display font-black text-xs">3</span>
                  <span>Paste {firstName}'s number, enter the amount, confirm.</span>
                </li>
              </ol>

              <div className="mt-4 text-[11px] text-neutral-600 leading-snug border-t border-dashed border-neutral-400 pt-3">
                Money goes <strong>directly</strong> to {firstName}'s bank. Curb Crew never sees, stores, or processes any payment data.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// Auto-redirect frame shown when a scanner lands on an unactivated card.
// The user explicitly asked us to drop the "Activate this card" interstitial
// (Feb 2026), so we replace it with an immediate router redirect. Renders a
// brief courtesy message so anyone with a slow connection or disabled JS
// timing still understands what's happening.
function NotActivatedRedirect({ code }) {
  const nav = useNavigate();
  useEffect(() => {
    nav(`/activate/${code}`, { replace: true });
  }, [code, nav]);
  return (
    <div>
      <Nav />
      <section className="border-b border-black bg-[color:var(--cc-yellow)]">
        <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="activate-redirect">
          <div className="label-tag">Card #{code}</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-3">
            Opening activator…
          </h1>
          <p className="mt-3 text-neutral-800 text-sm">
            One sec — taking you to the activation form.
          </p>
          <Link to={`/activate/${code}`} className="inline-flex mt-4 label-tag underline">
            Tap here if it doesn't load
          </Link>
        </div>
      </section>
    </div>
  );
}

// Curb Traveller doc-vault unlock card. Three states:
//   "locked"      — entry screen, asks for visitor name + optional note
//   "pin"         — PIN entry screen, shown after the request was emailed
//   "unlocked"    — final screen with a button that opens the Drive link
// We deliberately keep the URL OUT of state — once we get it, we open
// the new tab immediately and forget it, so a debugger can't grab it
// from React devtools after the fact.
function VaultUnlockCard({ code, cardholderName }) {
  const [stage, setStage] = useState("locked");
  const [visitorName, setVisitorName] = useState("");
  const [visitorNote, setVisitorNote] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [unlockedAt, setUnlockedAt] = useState(null);

  const requestPin = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!visitorName.trim()) { setError("Please type your name so they know who's asking."); return; }
    setBusy(true);
    try {
      await api.post(`/public/cardholder/${code}/vault/request`, {
        visitor_name: visitorName.trim(),
        visitor_note: visitorNote.trim(),
      });
      setStage("pin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitPin = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!/^\d{4,8}$/.test(pin)) { setError("The PIN is 6 digits."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/cardholder/${code}/vault/unlock`, { pin });
      // Open the link immediately in a new tab and DON'T keep it in
      // state. If pop-ups are blocked we fall back to a same-tab nav so
      // the visitor still gets the docs.
      if (data?.vault_link) {
        const w = window.open(data.vault_link, "_blank", "noopener,noreferrer");
        if (!w) window.location.href = data.vault_link;
      }
      setUnlockedAt(new Date());
      setStage("unlocked");
      setPin("");
    } catch (err) {
      setError(formatApiError(err));
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 border-2 border-[#0EA5E9] bg-[#F0F9FF] p-4" data-testid="vault-unlock-card">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="label-tag bg-[#0EA5E9] text-white px-2 py-1 inline-block">DOC VAULT · LOCKED</span>
        <span className="label-tag text-neutral-500">{cardholderName?.split(" ")[0]} keeps trip docs here</span>
      </div>

      {stage === "locked" && (
        <form onSubmit={requestPin} data-testid="vault-stage-locked">
          <div className="text-xs text-neutral-700 mb-3">
            Tap below to ask for the PIN. <strong>{cardholderName?.split(" ")[0]} gets the PIN by email</strong> and shares it with you verbally — Curb Crew never stores or shows the docs.
          </div>
          <div className="space-y-2">
            <div>
              <label className="label-tag">Your name <span className="text-neutral-500">(so they know who's asking)</span></label>
              <input
                value={visitorName}
                onChange={(e) => setVisitorName(e.target.value)}
                placeholder="Mrs Smith"
                maxLength={80}
                className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm"
                data-testid="vault-visitor-name"
              />
            </div>
            <div>
              <label className="label-tag">Reason <span className="text-neutral-500">(optional)</span></label>
              <input
                value={visitorNote}
                onChange={(e) => setVisitorNote(e.target.value)}
                placeholder="Hotel reception, need to see passport"
                maxLength={240}
                className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm"
                data-testid="vault-visitor-note"
              />
            </div>
          </div>
          {error && <div className="label-tag text-red-600 mt-2" data-testid="vault-error">{error}</div>}
          <button
            type="submit"
            disabled={busy}
            className="mt-3 inline-flex items-center justify-center gap-2 w-full bg-black text-white border-2 border-black px-4 py-3 brutal-btn label-tag tracking-wider disabled:opacity-50"
            data-testid="vault-request-btn"
          >
            {busy ? "Sending…" : "Request PIN"}
          </button>
        </form>
      )}

      {stage === "pin" && (
        <form onSubmit={submitPin} data-testid="vault-stage-pin">
          <div className="text-xs text-neutral-700 mb-3">
            We've emailed the 6-digit PIN to <strong>{cardholderName?.split(" ")[0]}</strong>. Ask them to read it out — then type it below. The PIN expires in 10 minutes.
          </div>
          <label className="label-tag">PIN</label>
          <input
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/[^\d]/g, "").slice(0, 8))}
            inputMode="numeric"
            placeholder="••••••"
            className="mt-1 w-full px-3 py-3 border-2 border-black outline-none text-2xl text-center font-mono-cc tracking-[0.4em]"
            autoFocus
            data-testid="vault-pin"
          />
          {error && <div className="label-tag text-red-600 mt-2" data-testid="vault-error">{error}</div>}
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <button
              type="submit"
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-black text-white border-2 border-black px-4 py-3 brutal-btn label-tag tracking-wider disabled:opacity-50"
              data-testid="vault-unlock-btn"
            >
              {busy ? "Unlocking…" : "Unlock vault"}
            </button>
            <button
              type="button"
              onClick={() => { setStage("locked"); setPin(""); setError(""); }}
              className="inline-flex items-center justify-center gap-2 bg-white text-black border-2 border-black px-4 py-3 brutal-btn label-tag tracking-wider"
              data-testid="vault-back-btn"
            >
              Back
            </button>
          </div>
        </form>
      )}

      {stage === "unlocked" && (
        <div data-testid="vault-stage-unlocked">
          <div className="text-sm text-neutral-800">
            Vault opened in a new tab at {unlockedAt?.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}. If your browser blocked the pop-up, ask {cardholderName?.split(" ")[0]} for a fresh PIN to try again.
          </div>
          <button
            type="button"
            onClick={() => { setStage("locked"); setVisitorName(""); setVisitorNote(""); }}
            className="mt-3 inline-flex items-center justify-center gap-2 bg-white text-black border-2 border-black px-4 py-3 brutal-btn label-tag tracking-wider"
            data-testid="vault-reset-btn"
          >
            Request another PIN
          </button>
        </div>
      )}
    </div>
  );
}

